import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CLIENT_STATUSES = [
    "active", "pending_docs", "submitted", "monitoring",
    "past_due", "cancelled", "completed", "inactive",
];
const PROGRAM_TYPES = ["unknown", "IDR", "IBR", "PAYE", "ICR", "Standard", "Other"];

export default function ConvertLeadDialog({ open, onOpenChange, lead, leadTypeNames = [] }) {
    const navigate = useNavigate();
    const [clientStatus, setClientStatus] = useState("active");
    const [programType, setProgramType] = useState("unknown");
    const [assignedRep, setAssignedRep] = useState("");
    const [copyContact, setCopyContact] = useState(true);
    const [copyNotes, setCopyNotes] = useState(true);
    const [linkComms, setLinkComms] = useState(true);
    const [preserveLead, setPreserveLead] = useState(true);
    const [busy, setBusy] = useState(false);

    if (!lead) return null;

    const hasProcessor = !!lead.processor_user_id;

    const submit = async () => {
        if (!hasProcessor) {
            toast.error("Assign a Processor before converting this lead to a client.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/clients/from-lead/${lead.id}`, {
                client_status: clientStatus,
                program_type: programType,
                assigned_rep: assignedRep || null,
                copy_contact: copyContact,
                copy_notes: copyNotes,
                link_communications: linkComms,
                preserve_lead: preserveLead,
            });
            const client = data.client || data;
            toast.success(data.already_converted ? "Lead is already a client" : "Lead converted to client");
            onOpenChange(false);
            navigate(`/clients/${client.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]" data-testid="convert-lead-dialog">
                <DialogHeader>
                    <DialogTitle>Convert Lead to Client</DialogTitle>
                    <DialogDescription>Promote this lead into the Clients CRM.</DialogDescription>
                </DialogHeader>

                <div className="rounded-md border border-border bg-gray-50 px-3 py-2 text-sm space-y-0.5">
                    <div className="font-medium text-gray-900">{lead.first_name} {lead.last_name}</div>
                    {lead.case_id && <div className="text-xs text-muted-foreground">Case ID: <span className="font-mono">{lead.case_id}</span></div>}
                    {(lead.phone_e164 || lead.phone_raw) && <div className="text-xs text-muted-foreground">Phone: {lead.phone_e164 || lead.phone_raw}</div>}
                    {lead.email && <div className="text-xs text-muted-foreground">Email: {lead.email}</div>}
                    {leadTypeNames.length > 0 && <div className="text-xs text-muted-foreground">Lead types: {leadTypeNames.join(", ")}</div>}
                    {lead.processor_name && <div className="text-xs text-muted-foreground">Processor: <span className="text-gray-900 font-medium">{lead.processor_name}</span></div>}
                    {lead.sales_rep_name && <div className="text-xs text-muted-foreground">Sales Rep: <span className="text-gray-900 font-medium">{lead.sales_rep_name}</span></div>}
                </div>

                {!hasProcessor && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2" data-testid="convert-needs-processor">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-200 text-amber-900 text-xs font-bold">!</span>
                        <span>Assign a Processor on the lead before converting.</span>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Client Status</label>
                        <Select value={clientStatus} onValueChange={setClientStatus}>
                            <SelectTrigger data-testid="convert-client-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{CLIENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Program Type</label>
                        <Select value={programType} onValueChange={setProgramType}>
                            <SelectTrigger data-testid="convert-program-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{PROGRAM_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Assigned Rep (optional)</label>
                    <Input value={assignedRep} onChange={e => setAssignedRep(e.target.value)} data-testid="convert-assigned-rep" />
                </div>

                <div className="space-y-2 pt-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <Checkbox checked={copyContact} onCheckedChange={v => setCopyContact(!!v)} data-testid="convert-copy-contact" />
                        Copy contact info
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <Checkbox checked={copyNotes} onCheckedChange={v => setCopyNotes(!!v)} data-testid="convert-copy-notes" />
                        Copy notes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <Checkbox checked={linkComms} onCheckedChange={v => setLinkComms(!!v)} data-testid="convert-link-comms" />
                        Link communication history
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <Checkbox checked={preserveLead} onCheckedChange={v => setPreserveLead(!!v)} data-testid="convert-preserve-lead" />
                        Preserve lead as converted
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !hasProcessor} data-testid="convert-confirm-button">
                        {busy ? "Converting…" : "Convert to Client"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
